import React, { useState, useEffect } from 'react';
import {
  Key, Plus, Copy, Eye, EyeOff, Trash2, MoreHorizontal,
  Shield, Clock, Globe, AlertTriangle, Check, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { apiV1, extractErrorMessage } from '@/api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Why the shared client and not raw fetch: SSO users hold only the
// linkhub_session cookie, so localStorage 'token' is null. Raw fetch sent
// "Authorization: Bearer null", the server tried to verify "null" as a JWT
// and returned 401 before ever looking at the cookie. apiV1 sends the cookie
// and only adds a Bearer header when a real token exists.
const errorMessage = (error, fallback) =>
  extractErrorMessage(error?.response?.data?.detail) || fallback;

const SCOPES = [
  { value: 'read', label: 'Read', description: 'View QR codes, URLs, pages, etc.' },
  { value: 'write', label: 'Write', description: 'Create and update items' },
  { value: 'delete', label: 'Delete', description: 'Delete items' },
  { value: 'admin', label: 'Admin', description: 'Full access including settings' },
];

export default function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: ['read', 'write'],
    expires_in_days: '',
    rate_limit_per_hour: 1000,
    org_wide: false,
  });

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const { data } = await apiV1.get('/api-keys/');
      if (data.success) {
        setKeys(data.data || []);
      } else {
        toast.error('Failed to load API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error(errorMessage(error, 'Failed to load API keys'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        scopes: formData.scopes,
        rate_limit_per_hour: formData.rate_limit_per_hour,
        org_wide: formData.org_wide,
      };

      if (formData.expires_in_days) {
        payload.expires_in_days = parseInt(formData.expires_in_days);
      }

      const { data } = await apiV1.post('/api-keys/', payload);
      if (data.success) {
        setNewKeyResult(data.data);
        setCreateOpen(false);
        setFormData({
          name: '',
          description: '',
          scopes: ['read', 'write'],
          expires_in_days: '',
          rate_limit_per_hour: 1000,
          org_wide: false,
        });
        fetchKeys();
      } else {
        toast.error('Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(errorMessage(error, 'Failed to create API key'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      const { data } = await apiV1.post(`/api-keys/${keyId}/revoke`);
      if (data.success) {
        toast.success('API key revoked');
        fetchKeys();
      } else {
        toast.error('Failed to revoke API key');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error(errorMessage(error, 'Failed to revoke API key'));
    }
  };

  const handleDelete = async () => {
    if (!deleteKey) return;

    setDeleting(true);
    try {
      const { data } = await apiV1.delete(`/api-keys/${deleteKey.id}`);
      if (data.success) {
        toast.success('API key deleted');
        fetchKeys();
      } else {
        toast.error('Failed to delete API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error(errorMessage(error, 'Failed to delete API key'));
    } finally {
      setDeleting(false);
      setDeleteKey(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleScope = (scope) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>;
      case 'revoked':
        return <Badge variant="destructive">Revoked</Badge>;
      case 'expired':
        return <Badge variant="secondary">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Key className="h-6 w-6" />
            API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for external integrations and programmatic access
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-api-key-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Shield className="h-10 w-10 text-blue-500 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Using API Keys</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Include your API key in the <code className="bg-muted px-1 rounded">X-API-Key</code> header 
                when making requests to the API. Keys are shown only once when created - store them securely.
              </p>
              <pre className="mt-3 p-3 bg-muted rounded-lg text-xs overflow-x-auto">
{`curl -X GET "${API_URL || window.location.origin}/api/v1/qrcodes/" \\
  -H "X-API-Key: lh_live_xxxxxxxxxxxxxxxxxxxx"`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your API Keys</CardTitle>
          <CardDescription>
            {keys.length} key{keys.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <h3 className="mt-4 font-medium">No API keys yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first API key to start integrating
              </p>
              <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id} data-testid="api-key-row">
                    <TableCell>
                      <div>
                        <div className="font-medium">{key.name}</div>
                        {key.description && (
                          <div className="text-xs text-muted-foreground">{key.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {key.key_prefix}...
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {key.scopes?.map((scope) => (
                          <Badge key={scope} variant="outline" className="text-xs">
                            {scope}
                          </Badge>
                        ))}
                        {key.org_wide && (
                          <Badge variant="secondary" className="text-xs" title="Sees every office's data">
                            all offices
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(key.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm">{key.usage_count?.toLocaleString() || 0} requests</span>
                    </TableCell>
                    <TableCell>
                      {key.last_used_at ? (
                        <span className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(key.last_used_at), { addSuffix: true })}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {key.status === 'active' && (
                            <DropdownMenuItem onClick={() => handleRevoke(key.id)}>
                              <AlertTriangle className="h-4 w-4 mr-2" />
                              Revoke
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteKey(key)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for external integrations. The key will only be shown once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Production Server"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="api-key-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="What is this key used for?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 gap-3">
                {SCOPES.map((scope) => (
                  <div
                    key={scope.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.scopes.includes(scope.value)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => toggleScope(scope.value)}
                  >
                    <Checkbox
                      checked={formData.scopes.includes(scope.value)}
                      onCheckedChange={() => toggleScope(scope.value)}
                    />
                    <div>
                      <div className="font-medium text-sm">{scope.label}</div>
                      <div className="text-xs text-muted-foreground">{scope.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <Checkbox
                id="org_wide"
                checked={formData.org_wide}
                onCheckedChange={(v) => setFormData({ ...formData, org_wide: v === true })}
              />
              <div>
                <Label htmlFor="org_wide" className="font-medium text-sm">All offices (org-wide)</Label>
                <p className="text-xs text-muted-foreground">
                  Without this, the key acts as you and sees only your regions and offices plus
                  what you created. Only admins can make an org-wide key.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expires">Expires In (days)</Label>
                <Input
                  id="expires"
                  type="number"
                  placeholder="Never"
                  min="1"
                  max="365"
                  value={formData.expires_in_days}
                  onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Leave empty for no expiration</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rate_limit">Rate Limit (per hour)</Label>
                <Input
                  id="rate_limit"
                  type="number"
                  min="10"
                  max="100000"
                  value={formData.rate_limit_per_hour}
                  onChange={(e) => setFormData({ ...formData, rate_limit_per_hour: parseInt(e.target.value) || 1000 })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Result Dialog */}
      <Dialog open={!!newKeyResult} onOpenChange={() => setNewKeyResult(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy your API key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-600 dark:text-amber-400">Important</p>
                  <p className="text-muted-foreground">
                    This is the only time you'll see this key. Store it securely - 
                    we cannot recover it if lost.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Your API Key</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={newKeyResult?.api_key || ''}
                    readOnly
                    className="pr-10 font-mono text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(newKeyResult?.api_key)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>
                <span className="ml-2 font-medium">{newKeyResult?.name}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Scopes:</span>
                <span className="ml-2">{newKeyResult?.scopes?.join(', ')}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setNewKeyResult(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteKey} onOpenChange={() => setDeleteKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteKey?.name}"? Any applications using this key will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
